import { ChangeEvent, FocusEvent, useRef } from 'react'

type FormFieldProps = {
  kind?: 'input' | 'select' | 'textarea'
  type?: string
  placeholder?: string
  options?: string[]
  iconSrc: string
  themeColor?: 'purple' | 'green' | 'rabbitx' | 'arbventure' | 'tatvacaretheme' | 'ddxtheme' | 'medilearntheme'
  fieldClassName?: string
  iconClassName?: string
  inputClassName?: string
  value?: string
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  name?: string
}

const FormField = ({
  kind = 'input',
  type = 'text',
  placeholder = '',
  options = [],
  iconSrc,
  themeColor = 'purple',
  fieldClassName = '',
  iconClassName = '',
  inputClassName = '',
  value = '',
  onChange,
  onBlur,
  name,
}: FormFieldProps) => {
  const inputElementRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const themeConfig: Record<
    'purple' | 'green' | 'rabbitx' | 'arbventure' | 'tatvacaretheme' | 'ddxtheme' | 'medilearntheme',
    {
      wrapperClassName: string
      iconClassName: string
      dividerClassName: string
      inputClassName: string
      textAreaClassName: string
    }
  > = {
    purple: {
      wrapperClassName: `
        w-full rounded-xl 
        bg-gradient-to-r from-[rgb(24,19,61)] to-[rgb(17,14,41)]
        px-4 py-3 flex items-center gap-3 
        transition-all duration-200 
        focus-within:border-[#8f7bff] 
        focus-within:ring-2 focus-within:ring-[#745EF0]/20
      `,
      iconClassName: 'bg-gray-400',
      dividerClassName: 'bg-gray-500/60',
      inputClassName:
        'w-full bg-transparent text-sm text-white font-gilroy placeholder:font-gilroy placeholder:text-gray-400 outline-none',
      textAreaClassName:
        'w-full bg-transparent text-sm md:text-base text-white font-gilroy placeholder:font-gilroy-medium placeholder:text-gray-400 outline-none resize-none',
    },
    green: {
      wrapperClassName:
        'w-full rounded-xl bg-white/2 px-4 py-3 flex items-center gap-3 transition-all duration-200 focus-within:border-[#8FE64A] focus-within:ring-2 focus-within:ring-[#8FE64A]/20',
      iconClassName: 'bg-gray-300',
      dividerClassName: 'bg-gray-400/60',
      inputClassName:
        'w-full bg-transparent text-sm text-white font-gilroy placeholder:font-gilroy placeholder:text-gray-300 outline-none',
      textAreaClassName:
        'w-full mbg-transparent text-sm md:text-base text-white font-gilroy placeholder:font-gilroy-medium placeholder:text-gray-300 outline-none resize-none',
    },
    rabbitx: {
      wrapperClassName:
        'w-full rounded-xl bg-white/2 px-4 py-3 flex items-center gap-3 transition-all duration-200 focus-within:border-[#EE344C] focus-within:ring-2 focus-within:ring-[#EE344C]/20',
      iconClassName: 'bg-gray-300',
      dividerClassName: 'bg-gray-400/60',
      inputClassName:
        'w-full bg-transparent text-sm text-white font-gilroy placeholder:font-gilroy placeholder:text-gray-300 outline-none',
      textAreaClassName:
        'w-full mbg-transparent text-sm md:text-base text-white font-gilroy placeholder:font-gilroy-medium placeholder:text-gray-300 outline-none resize-none',
    },
    arbventure: {
      wrapperClassName:
        'w-full rounded-xl bg-white/2 px-4 py-3 flex items-center gap-3 transition-all duration-200 focus-within:border-[#5F9937] focus-within:ring-2 focus-within:ring-[#5F9937]/20',
      iconClassName: 'bg-gray-300',
      dividerClassName: 'bg-gray-400/60',
      inputClassName:
        'w-full bg-transparent text-sm text-white font-gilroy placeholder:font-gilroy placeholder:text-gray-300 outline-none',
      textAreaClassName:
        'w-full mbg-transparent text-sm md:text-base text-white font-gilroy placeholder:font-gilroy-medium placeholder:text-gray-300 outline-none resize-none',
    },
    tatvacaretheme: {
      wrapperClassName:
        'w-full rounded-xl bg-[#e7a4e9ce]/20 px-4 py-3 flex items-center gap-3 transition-all duration-200 focus-within:border-[#E8B4F0] focus-within:ring-2 focus-within:ring-[#A461D8]/25',
      iconClassName: 'bg-gray-300',
      dividerClassName: 'bg-gray-400/60',
      inputClassName:
        'w-full bg-transparent text-sm text-white font-gilroy placeholder:font-gilroy placeholder:text-gray-300 outline-none',
      textAreaClassName:
        'w-full mbg-transparent text-sm md:text-base text-white font-gilroy placeholder:font-gilroy-medium placeholder:text-gray-300 outline-none resize-none',
    },
    ddxtheme: {
      wrapperClassName:
        'w-full rounded-xl bg-[#6e3f92]/30 px-4 py-3 flex items-center gap-3 transition-all duration-200 focus-within:border-[#9B3FD4] focus-within:ring-2 focus-within:ring-[#5E0F9B]/30',
      iconClassName: 'bg-gray-300',
      dividerClassName: 'bg-gray-400/60',
      inputClassName:
        'w-full bg-transparent text-sm text-white font-gilroy placeholder:font-gilroy placeholder:text-gray-300 outline-none',
      textAreaClassName:
        'w-full mbg-transparent text-sm md:text-base text-white font-gilroy placeholder:font-gilroy-medium placeholder:text-gray-300 outline-none resize-none',
    },
    medilearntheme: {
      wrapperClassName:
        'w-full rounded-xl bg-[#c4d899d8]/30 px-4 py-3 flex items-center gap-3 transition-all duration-200 focus-within:border-[#B6C887] focus-within:ring-2 focus-within:ring-[#9BB069]/30',
      iconClassName: 'bg-gray-300',
      dividerClassName: 'bg-white/30',
      inputClassName:
        'w-full bg-transparent text-sm text-white font-gilroy placeholder:font-gilroy placeholder:text-gray-300 outline-none',
      textAreaClassName:
        'w-full mbg-transparent text-sm md:text-base text-white font-gilroy placeholder:font-gilroy-medium placeholder:text-gray-300 outline-none resize-none',
    },
  }
  const selectedTheme = themeConfig[themeColor] ?? themeConfig.purple

  const iconMaskStyle = {
    WebkitMaskImage: `url(${iconSrc})`,
    maskImage: `url(${iconSrc})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
  } as const

  if (kind === 'textarea') {
    return (
      <div
        className={`${selectedTheme.wrapperClassName} ${fieldClassName} items-start py-4`.trim()}
        onClick={() => inputElementRef.current?.focus()}
      >
        <span
          className={`mt-1 h-5 w-5 min-w-5 ${selectedTheme.iconClassName} ${iconClassName}`.trim()}
          style={iconMaskStyle}
        />
        <span className={`h-6 w-px ${selectedTheme.dividerClassName}`} />
        <textarea
          ref={(node) => { inputElementRef.current = node }}
          name={name}
          placeholder={placeholder}
          className={`${selectedTheme.textAreaClassName} ${inputClassName}`.trim()}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
        />
      </div>
    )
  }


  return (
    <div
      className={`${selectedTheme.wrapperClassName} ${fieldClassName}`.trim()}
      onClick={() => inputElementRef.current?.focus()}
    >
      <span
        className={`h-10 w-5 min-w-5 ${selectedTheme.iconClassName} ${iconClassName}`.trim()}
        style={iconMaskStyle}
      />
      <span className={`h-10 w-px ${selectedTheme.dividerClassName}`} />
      <input
        ref={(node) => { inputElementRef.current = node }}
        name={name}
        type={type}
        placeholder={placeholder}
        className={`${selectedTheme.inputClassName} ${inputClassName}`.trim()}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
    </div>
  )
}

export default FormField
